import React from 'react'
import { useTranslation } from 'react-i18next'

import { IconS24Crane224 } from '@is24/cosma-ui-icons'
import { DropdownValue, TooltipTypes } from '../../../../../@types/types'
import { ConstructionScreenValidationResult } from '../../../../../@types/validation-types'
import { EnergySourceOption } from '../../../../../@types/dropdown-types'
import { InsertionFlowScreensProps } from '../../../../../@types/props-types'
import EnergyCertificate from './energyCertificate/EnergyCertificate'
import { SCREEN_CONSTRUCTION, SCREENS } from '../../../../../services/domain/wizardScreens'
import ScreenContainer from '../../../../common/containers/screenContainer/ScreenContainer'
import { ScreenLayout } from '../../../../common/containers/screenLayout/ScreenLayout'
import ConstructionConditionField from './fields/constructionConditionField/ConstructionConditionField'
import LastModernizationField from '../commonFields/lastModernizationField/LastModernizationField'
import ConstructionYearField from '../commonFields/constructionYearField/ConstructionYearField'
import HeatingTypeField from '../commonFields/heatingType/HeatingTypeField'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import { ScreenTooltip } from '../../../../common/display/screenTooltip/ScreenTooltip'
import MultiSelectDropdown from '../../../../common/inputs/multiSelectDropdown/MultiSelectDropdown'

const screen = SCREENS[SCREEN_CONSTRUCTION]
type AdditionalConstructionProps = {
  hasLastModernizationField?: boolean
}
const Construction: React.FC<
  InsertionFlowScreensProps<ConstructionScreenValidationResult> & AdditionalConstructionProps
> = ({ insertionData, validationMessages, onChange, doReset, onNavigate, hasLastModernizationField }) => {
  const { t } = useTranslation()
  const displayAppropriateTooltip = (tooltipType: TooltipTypes) => {
    return <ScreenTooltip tooltipType={tooltipType} tooltipKey='tooltip.screens.construction.common' />
  }

  return (
    <ScreenContainer containerId={screen.id} onNavigate={onNavigate} screen={screen} className=''>
      <ScreenLayout
        id={`${screen.id}-desk-tooltip`}
        headline={'wizard.screen.construction.header'}
        deskTooltip={displayAppropriateTooltip(TooltipTypes.DESKTOP)}
        mobileTooltip={displayAppropriateTooltip(TooltipTypes.MOBILE)}
        className='grid gutter-vertical-l'
        headerIcon={<IconS24Crane224 />}
        screenName={screen.name}
      >
        <ConstructionConditionField
          insertionData={insertionData}
          onChange={onChange}
          screenName={screen.name}
          additionalClassNames={'lap-one-half lap-padding-right-m desk-padding-right-m'}
        />
        {hasLastModernizationField && (
          <LastModernizationField
            insertionData={insertionData}
            validationMessages={validationMessages}
            onChange={onChange}
            screenName={screen.name}
          />
        )}
        <HeatingTypeField
          insertionData={insertionData}
          screenName={SCREENS[SCREEN_CONSTRUCTION].name}
          onChange={onChange}
          wrapperClassName={hasLastModernizationField ? getDefaultClasses('left') : getDefaultClasses('right')}
        />
        <div className={hasLastModernizationField ? getDefaultClasses('right') : getDefaultClasses('left')}>
          <MultiSelectDropdown
            id='construction-energy-source'
            label={'wizard.screen.construction.energySource'}
            labelClassName={'font-ellipsis'}
            name='constructionEnergySource'
            values={insertionData.constructionEnergySource?.map((source) => source as DropdownValue)}
            noInformation={EnergySourceOption.NO_INFORMATION}
            options={{
              translationKeyGroup: 'wizard.screen.construction.energySourceTypes',
              options: Object.keys(EnergySourceOption),
            }}
            onChange={(values: DropdownValue[]) =>
              onChange({ screen: screen.name, property: 'constructionEnergySource' }, values)
            }
            allowEmpty={true}
          />
        </div>
        <ConstructionYearField
          insertionData={insertionData}
          validationMessages={validationMessages}
          onChange={onChange}
          screenName={screen.name}
          wrapperClassName={hasLastModernizationField ? getDefaultClasses('left') : getDefaultClasses('right')}
        />
        <div className='grid-item one-whole'>
          <EnergyCertificate
            screenId={screen.id}
            insertionData={insertionData}
            validators={validationMessages!}
            onEnergyCertificateChange={(field, value) => onChange({ screen: screen.name, property: field }, value)}
            doReset={doReset!}
          />
        </div>
        <div className='grid-item one-whole margin-bottom-none'>
          <span>
            {t(
              'wizard.screen.construction.hint',
              'The Building Energy Law GEG 2020 requires that all relevant information from the energy certificate must be presented. The energy certificate must be presented without being asked at the latest during the viewing.'
            )}
          </span>
        </div>
      </ScreenLayout>
    </ScreenContainer>
  )
}

export default Construction
